import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/products/shared/services/supabase/client';
import type { Permission, UserRole } from './permissions';

interface RBACState {
  role: UserRole | null;
  permissions: Set<Permission>;
  loading: boolean;
  refresh: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isSuperAdmin: () => boolean;
}

const RBACContext = createContext<RBACState>({
  role: null,
  permissions: new Set(),
  loading: true,
  refresh: async () => {},
  hasPermission: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  isSuperAdmin: () => false,
});

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set());
  const [loading, setLoading] = useState(true);

  // Keep track of the user_id we fetched last so we can avoid redundant calls
  const lastUserIdRef = useRef<string | null>(null);

  const fetchRoleAndPermissions = useCallback(async () => {
    // console.log("fetchroleandpermissions called again?")
    setLoading(true);
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        // Local storage still has a session, but the server no longer recognizes it
        // (e.g. the referenced auth user was removed by a local DB reset). Treat this
        // as logged out instead of "authenticated with no role", which previously sent
        // guards like RedirectIfAuthenticated down the /unauthorized path and hid the
        // sign-in page entirely.
        console.warn('Stale/unverifiable session detected, signing out:', userError.message);
        await supabase.auth.signOut();
        setRole(null);
        setPermissions(new Set());
        setLoading(false);
        return;
      }

      if (!user) {
        setRole(null);
        setPermissions(new Set());
        setLoading(false);
        return;
      }

      // 1️⃣ Fetch role from user_roles table
      const { data: roleRows, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (roleError) {
        console.error('Error fetching user role:', roleError);
        setRole(null);
        setPermissions(new Set());
        setLoading(false);
        return;
      }

      const distinctRoles = [...new Set(
        (roleRows ?? [])
          .map((row) => row.role)
          .filter((value): value is UserRole => typeof value === 'string'),
      )];
      if (distinctRoles.length !== 1) {
        if (distinctRoles.length > 1) {
          console.error('Multiple distinct roles for user', user.id, distinctRoles);
        }
        setRole(null);
        setPermissions(new Set());
        setLoading(false);
        return;
      }

      const currentRole = distinctRoles[0];
      setRole(currentRole);

      // 2️⃣ Fetch all permissions for that role
      const { data: permissionData, error: permissionError } = await supabase
        .from('role_permissions')
        .select('permission')
        .eq('role', currentRole);

      if (permissionError) {
        console.error('Error fetching role permissions:', permissionError);
        setPermissions(new Set());
      } else {
        const userPermissions = new Set(
          permissionData?.map(row => row.permission) as Permission[]
        );
        setPermissions(userPermissions);
      }
    } catch (error) {
      console.error('Error in fetchRoleAndPermissions:', error);
      setRole(null);
      setPermissions(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper functions - memoized to prevent infinite re-renders
  const hasPermission = useCallback((permission: Permission): boolean => {
    return permissions.has(permission);
  }, [permissions]);

  const hasRole = useCallback((requiredRole: UserRole): boolean => {
    return role === requiredRole;
  }, [role]);

  const hasAnyRole = useCallback((allowedRoles: UserRole[]): boolean => {
    return role !== null && allowedRoles.includes(role);
  }, [role]);

  const isSuperAdmin = useCallback((): boolean => {
    return role === 'super_admin';
  }, [role]);
// ① run once when the provider mounts
useEffect(() => {
  fetchRoleAndPermissions();

  // ② run again every time Supabase tells us the auth-state changed
  const { data: { subscription } } =
    supabase.auth.onAuthStateChange((event, session) => {
      // Always refresh on profile change or token refresh
      if (event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
        fetchRoleAndPermissions();
        return;
      }

      if (event === 'SIGNED_IN') {
        // Only fetch if we haven't already fetched for this user
        const currentUserId = session?.user?.id ?? null;
        if (currentUserId && currentUserId !== lastUserIdRef.current) {
          lastUserIdRef.current = currentUserId;
          fetchRoleAndPermissions();
        }
        return;
      }

      if (event === 'INITIAL_SESSION') {
        fetchRoleAndPermissions();
      }
    });

  return () => subscription.unsubscribe();
}, []);
  // useEffect(() => {
  //   // console.log("useeffect called again?")
  //   fetchRoleAndPermissions();

  //   // Listen for auth state changes
  //   supabase.auth.onAuthStateChange((event) => {
  //     if (event === 'INITIAL_SESSION'   // first load
  //         || event === 'USER_UPDATED'   // profile change
  //         || event === 'TOKEN_REFRESHED') {
  //       fetchRoleAndPermissions();
  //     }
  //   });

  //   return () => {
  //     // The subscription is not directly returned by onAuthStateChange,
  //     // so we can't unsubscribe here in the same way.
  //     // The original code had a placeholder for subscription.unsubscribe(),
  //     // but it was not directly available from onAuthStateChange.
  //     // For now, removing the line as it's not directly applicable.
  //   };
  // }, []);
  
  const contextValue: RBACState = useMemo(() => ({
    role,
    permissions,
    loading,
    refresh: fetchRoleAndPermissions,
    hasPermission,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
  }), [role, permissions, loading, fetchRoleAndPermissions, hasPermission, hasRole, hasAnyRole, isSuperAdmin]);

  return (
    <RBACContext.Provider value={contextValue}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}; 